/**
 * QuizBlank Component
 * Ported from reference_legacy/quebon-lms-web/components/lesson/quiz-types/shared/quiz-blank.tsx
 */

import { cn } from '../../../lib/utils'
import { useRef, useEffect, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { QUIZ_INLINE_STYLES } from './quiz-styles'

interface QuizBlankProps {
  index: number
  answers: string[]
  currentIndex: number
  isCorrect?: boolean | null
  isSolved?: boolean
  showCorrectAnswer?: boolean
  correctAnswer?: string
  onClick?: () => void
  onChange?: (value: string) => void
  useKeypad?: boolean
  minWidth?: string
  width?: string
  isViewSolved?: boolean
  height?: string
}

// Custom comparison: only re-render if THIS blank's value changed or if active state changed
function arePropsEqual(prevProps: QuizBlankProps, nextProps: QuizBlankProps): boolean {
  const { index } = prevProps
  
  // Check if this specific blank's value changed
  const prevValue = prevProps.answers[index] || ''
  const nextValue = nextProps.answers[index] || ''
  if (prevValue !== nextValue) return false
  
  // Check if active state changed (currentIndex affects isActive)
  const prevIsActive = prevProps.currentIndex === index && !prevProps.isSolved
  const nextIsActive = nextProps.currentIndex === index && !nextProps.isSolved
  if (prevIsActive !== nextIsActive) return false
  
  // Check other props that affect rendering
  if (prevProps.isSolved !== nextProps.isSolved) return false
  if (prevProps.isCorrect !== nextProps.isCorrect) return false
  if (prevProps.showCorrectAnswer !== nextProps.showCorrectAnswer) return false
  if (prevProps.correctAnswer !== nextProps.correctAnswer) return false
  if (prevProps.useKeypad !== nextProps.useKeypad) return false
  if (prevProps.isViewSolved !== nextProps.isViewSolved) return false
  if (prevProps.minWidth !== nextProps.minWidth) return false
  if (prevProps.width !== nextProps.width) return false
  if (prevProps.height !== nextProps.height) return false
  
  // onClick and onChange are stable due to useCallback in parent
  return true
}

export const QuizBlank = memo(function QuizBlank({
  index,
  answers,
  currentIndex,
  isCorrect,
  isSolved,
  showCorrectAnswer,
  correctAnswer,
  onClick,
  onChange,
  useKeypad = false,
  minWidth = '56px',
  width,
  isViewSolved = false,
  height,
}: QuizBlankProps) {
  const { t } = useTranslation('quiz')
  const inputRef = useRef<HTMLInputElement>(null)
  const value = answers[index] || ''
  const isActive = currentIndex === index && !isSolved
  const hasValue = value !== ''
  
  // 정답 표시 모드 - 정답보기 시 전체 빈칸을 correctAnswer로 교체
  const displayValue = showCorrectAnswer && isSolved && correctAnswer ? correctAnswer : value

  // 활성화 시 input focus
  useEffect(() => {
    if (isActive && !useKeypad && inputRef.current) {
      inputRef.current.focus({ preventScroll: true })
    }
  }, [isActive, useKeypad])

  // 직접 입력 모드 - 기획 데모 .inline-input 스타일
  if (!useKeypad && !isSolved && !isViewSolved) {
    // input size 속성으로 너비 자동 조절 (최소 4자)
    const inputSize = Math.max(value.length || 4, 4)
    
    return (
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onClick={onClick}
        placeholder={t('shared.blank')}
        size={inputSize}
        className={cn(
          'inline-flex items-center justify-center py-1 px-2 align-middle bg-white border rounded-lg mx-1 text-base font-semibold text-gray-900 transition-all text-center outline-hidden',
          QUIZ_INLINE_STYLES.inputPlaceholder,
          'cursor-text',
          // 기본 상태
          !isActive && !hasValue && 'border-gray-300',
          isActive && 'border-blue-500 ring-2 ring-blue-100',
          hasValue && !isActive && 'border-blue-500 bg-blue-50 text-blue-700'
        )}
        style={{ 
          minWidth: width || minWidth,
          height: height || 'auto',
        }}
      />
    )
  }

  // span은 inline-block으로 내용에 맞게 자동 확장
  return (
    <span
      onClick={onClick}
      className={cn(
        'inline-flex items-center justify-center py-1 px-3 align-middle bg-white border rounded-lg mx-1 text-base font-semibold text-gray-900 transition-all text-center outline-hidden',
        !isSolved && !isActive && !hasValue && 'border-gray-300 cursor-pointer',
        isActive && 'border-blue-500 ring-2 ring-blue-100 scale-105 cursor-pointer',
        !isSolved && hasValue && !isActive && 'border-blue-500 bg-blue-50 text-blue-700 cursor-pointer',
        // 정답보기 모드 - 전체 빈칸을 정답(초록색)으로 표시
        showCorrectAnswer && isSolved && QUIZ_INLINE_STYLES.correct,
        // 정답 (내 답안이 맞음) - 정답보기 모드가 아닐 때
        !showCorrectAnswer && isSolved && isCorrect === true && QUIZ_INLINE_STYLES.correct,
        // 오답 (정답보기 모드가 아닐 때만 빨강)
        !showCorrectAnswer && isSolved && isCorrect === false && QUIZ_INLINE_STYLES.wrong,
        // 비활성화
        (isSolved || isViewSolved) && 'pointer-events-none'
      )}
      style={{ 
        minWidth: width || minWidth,
        height: height || 'auto',
      }} 
    >
      {displayValue || (
        <span className={QUIZ_INLINE_STYLES.boxEmpty}>{t('shared.blank')}</span>
      )}
    </span>
  )
}, arePropsEqual)
